import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import ConsumersCard from "./owner-dialog/ConsumersCard";
import OwnerDetailsForm from "./owner-dialog/OwnerDetailsForm";
import RelatedCard from "./owner-dialog/RelatedCard";
import OwnerSummary from "./owner-dialog/OwnerSummary";
import RelatedTable from "./owner-dialog/RelatedTable";
import OwnerDialogHooks from "./owner-dialog/hooks";
import IyoFormSection from "../../common/IyoFormSection";
import BusinessService from "../../../apis/BusinessService";
import ConsumersService from "../../../apis/ConsumerService";
import OwnerConsumersService from "../../../apis/OwnerConsumersService";
import OwnersService from "../../../apis/OwnersService";
import OwnerAssociationsService from "../../../apis/OwnerAssociationService";
import RocksetService from "../../../apis/RocksetService";
import Auth from "../../../auth/Auth";

// Read-only views of what an owner owns. All four sit on the page at once rather than
// behind tabs: the question the view answers - what does this owner actually have - is not
// worth three clicks, and each list is short enough to box and scroll on its own.
//
// Columns are kept to what identifies a record. The page each list links to is where the
// rest of the columns live.
const DATA_STREAM_COLUMNS = [
  {field: 'name', headerName: 'Name'},
  {field: 'provider_name', headerName: 'Provider'},
  {field: 'cron', headerName: 'Schedule'},
];

const DEVICE_COLUMNS = [
  {field: 'mac_address', headerName: 'MAC address'},
  {field: 'active', headerName: 'Active', render: (row) => (row.active ? 'Yes' : 'No')},
  {field: 'activation_date', headerName: 'Activated'},
  {field: 'health_timestamp', headerName: 'Last health'},
];

const LOCATION_COLUMNS = [
  {field: 'name', headerName: 'Name'},
  {field: 'id', headerName: 'Owner ID', width: 110},
  {field: 'timezone', headerName: 'Time zone'},
];

const OwnerEditDialog = ({ open, setOpen, rowEditing, mode, handleError, parentOwnerId }) => {
  const [selectedConsumers, setSelectedConsumers] = useState([]);

  const history = useHistory();
  const queryClient = useQueryClient();
  const getTimezones = useQuery(
    "getTimezones",
    async () => {
      const data = await RocksetService.executeQuery("timezones");
      const sortedNames = data.map((rec) => rec.name).sort();
      return data
        .sort(
          (a, b) => sortedNames.indexOf(a.name) - sortedNames.indexOf(b.name)
        )
        .map((tz) => ({ id: tz._id, ...tz }));
    },
    {
      onError: (error) => {
        handleError(error);
      },
      retry: false,
      staleTime: Infinity,
    }
  );

  const getBusinesses = useQuery(
    "getBusinesses",
    async () => BusinessService.getAll(),
    {
      onError: (error) => {
        handleError(error);
      },
      retry: false,
      staleTime: Infinity,
    }
  );

  const consumersQuery = useQuery(
    ["allConsumers"],
    async () => ConsumersService.getAll(),
    { cacheTime: Infinity }
  );

  const consumersForOwner = useQuery(
    ["consumers-by-owner", rowEditing?.id],
    () => OwnerConsumersService.get(rowEditing?.id),
    { enabled: !!rowEditing?.id }
  );

  const form = useForm({
    name: rowEditing?.name || "",
    description: rowEditing?.description || "",
    business_id: rowEditing?.businessId || "",
    timezone: rowEditing?.timezone
      ? getTimezones.data?.find((tz) => tz.name === rowEditing?.timezone)
          ?.name || ""
      : "",
  });

  useEffect(() => {
    if (getTimezones.isSuccess) {
      const formVals = {
        name: rowEditing?.name || "",
        description: rowEditing?.description || "",
        business_id: rowEditing?.business_id || "",
        timezone: rowEditing?.timezone
          ? getTimezones.data?.find((tz) => tz.name === rowEditing?.timezone)
              ?.name || ""
          : "",
        multi_site: rowEditing?.multi_site || 0,
      };
      form.reset(formVals);
      if (!rowEditing) setSelectedConsumers([]);
    }
  }, [rowEditing?.id, getTimezones.data]);

  useEffect(() => {
    if (consumersQuery.data?.length) {
      setSelectedConsumers(
        consumersQuery.data.filter(
          (x) =>
            consumersForOwner.data?.find((y) => x.id === y.consumer_id) !==
            undefined
        )
      );
    }
  }, [consumersQuery.data?.length, consumersForOwner.data]);

  const updateConsumers = useMutation(async (ownerId) => {
    const ownerConsumersFromDb = consumersForOwner.data;
    const selectedIds = selectedConsumers.map((consumer) => consumer.id);

    const toDeletePromises =
      ownerConsumersFromDb
        ?.filter((consumer) => !selectedIds.includes(consumer.consumer_id))
        .map((x) => OwnerConsumersService.del(ownerId, x.consumer_id)) || [];
    const toAddPromises = selectedIds
      .filter((consumerId) =>
        ownerConsumersFromDb !== undefined
          ? !ownerConsumersFromDb.some(
              (dbConsumer) => dbConsumer.consumer_id === consumerId
            )
          : true
      )
      .map((x) => OwnerConsumersService.post(ownerId, x));

    await Promise.all(toDeletePromises.concat(toAddPromises));
    queryClient.invalidateQueries("consumers-by-owner");
    setOpen(false);
  });

  const createOwnerAssociation = async (parentId, childId) => {
    try {
      const user = await Auth.getUser();
      const associationData = {
        parent_owner_id: parentId,
        child_owner_id: childId,
        updated_by: user
      };
      return await OwnerAssociationsService.post(associationData);
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  const postOwner = useMutation(
    "postOwner",
    async (data) => OwnersService.post(data),
    {
      onSuccess: async (res) => {
        await queryClient.invalidateQueries("getOwners");

        if (parentOwnerId) {
          await createOwnerAssociation(parentOwnerId, res.id);
        }
        updateConsumers.mutate(res.id);
      },
      onError: (error) => {
        handleError(error);
      },
    }
  );

  const putOwner = useMutation(
    "putOwner",
    async (data) => OwnersService.put(rowEditing.id, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries("getOwners");
        updateConsumers.mutate(rowEditing.id);
      },
      onError: (error) => {
        handleError(error);
      },
    }
  );

  const handleCreateSubmit = async () => {
    const values = form.getValues();
    const body = {
      name: values.name,
      description: values.description || undefined,
      business_id: values.business_id || undefined,
      timezone: values.timezone,
      multi_site: values.multi_site || 0, 
      created_by: await Auth.getUser(),
      updated_by: await Auth.getUser(),
    };
    postOwner.mutate(body);
    // SET SPINNER
  };

  const handleEditSubmit = async () => {
    const values = form.getValues();

    const _values = JSON.parse(JSON.stringify(values));
    _values.description = values.description || undefined;
    _values.timezone = _values.timezone || undefined;
    _values.business_id = _values.business_id || undefined;

    const changed = [];
    for (const field in _values) {
      if (_values[field] !== rowEditing[field]) {
        changed.push([field, _values[field]]);
      }
    }

    if (changed.length === 0) {
      setOpen(false);
      return;
    }

    changed.push(["updated_by", await Auth.getUser()]);
    changed.push(["id", rowEditing.id]);
    const body = Object.fromEntries(changed);

    putOwner.mutate(body);

    // SET SPINNER
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Related records, fetched only while an existing owner is open.
  const isEdit = mode === "edit" && Boolean(rowEditing?.id);
  const relations = OwnerDialogHooks.useOwnerRelations(rowEditing, open && isEdit);

  // Leaves for the page that owns the record: these lists are a way in, not a second editor.
  const openElsewhere = (path) => {
    setOpen(false);
    history.push(path);
  };

  const businessName = getBusinesses.data?.find((b) => b.id === rowEditing?.business_id)?.name;

  const stats = [
    {label: "Data streams", value: relations.dataStreams.length},
    {label: "Devices", value: relations.devices.length},
    {label: "Consumers", value: selectedConsumers.length},
    {label: "Locations", value: relations.locations.length},
  ];

  const handleSubmit = () => {
    if (mode === "edit") {
      handleEditSubmit();
    } else if (mode === "create") {
      handleCreateSubmit();
    } else {
      console.error("Invalid edit type");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth={true}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{ display: "flex", alignItems: "flex-start", gap: 1, pr: 1 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {isEdit ? rowEditing.name || `Owner ${rowEditing.id}` : "Create Owner"}
          </Typography>
          {isEdit && (
            <Typography variant="caption" color="textSecondary">
              Owner {rowEditing.id}
              {rowEditing.description ? ` · ${rowEditing.description}` : ""}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close" size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isEdit && (
          <OwnerSummary
            owner={rowEditing}
            businessName={businessName}
            stats={stats}
            loading={relations.dataStreamsLoading || relations.devicesLoading}
          />
        )}

        <Box sx={{ mb: 2 }}>
          <IyoFormSection title="Details" description="What this owner is called, and where it sits.">
            <OwnerDetailsForm
              form={form}
              businesses={getBusinesses.data}
              timezones={getTimezones.data}
            />
          </IyoFormSection>
        </Box>

        {/* Two to a row on a wide dialog, stacked on a narrow one. Cards stretch to the
            tallest in their row so the grid stays square. */}
        <Grid container spacing={2} alignItems="stretch">
          {isEdit && (
            <Grid item xs={12} md={6}>
              <RelatedCard title="Data streams" count={relations.dataStreams.length}>
                <RelatedTable
                  columns={DATA_STREAM_COLUMNS}
                  rows={relations.dataStreams}
                  loading={relations.dataStreamsLoading}
                  emptyText="No data streams belong to this owner."
                  onOpen={(row) => openElsewhere(`/data-streams?id=${row.id}`)}
                />
              </RelatedCard>
            </Grid>
          )}

          {isEdit && (
            <Grid item xs={12} md={6}>
              <RelatedCard title="Devices" count={relations.devices.length}>
                <RelatedTable
                  columns={DEVICE_COLUMNS}
                  rows={relations.devices}
                  loading={relations.devicesLoading}
                  emptyText="No devices are registered to this owner."
                />
              </RelatedCard>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <ConsumersCard
              consumers={consumersQuery.data}
              selected={selectedConsumers}
              setSelected={setSelectedConsumers}
            />
          </Grid>

          {isEdit && rowEditing.multi_site === 1 && (
            <Grid item xs={12} md={6}>
              <RelatedCard title="Locations" count={relations.locations.length}>
                <RelatedTable
                  columns={LOCATION_COLUMNS}
                  rows={relations.locations}
                  loading={relations.locationsLoading}
                  emptyText="No child locations are associated with this owner."
                  onOpen={(row) => openElsewhere(`/owners?id=${row.id}`)}
                />
              </RelatedCard>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          color="primary"
          disabled={postOwner.isLoading || putOwner.isLoading || updateConsumers.isLoading}
          onClick={handleClose}
          size="large"
          type="button"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={postOwner.isLoading || putOwner.isLoading || updateConsumers.isLoading}
          size="large"
          variant="contained"
          onClick={handleSubmit}
        >
          Save
          {(postOwner.isLoading || putOwner.isLoading) && (
            <CircularProgress size={15} sx={{ ml: "8px" }} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OwnerEditDialog;